"use client";

import dynamic from "next/dynamic";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { buildAsteroidSumChallenge } from "@/lib/game/asteroidSum";
import { playSound } from "@/lib/game/audio";
import { formatStarReward } from "@/lib/game/stars";
import { rocketPlacementsForDisplay } from "@/lib/game/rocket";
import { ActivityLayout } from "./ActivityLayout";
import { ActivitySuccessOverlay } from "./ActivitySuccessOverlay";
import { DifficultyPicker } from "./DifficultyPicker";
import type { AsteroidSumSceneHandle } from "./PixiAsteroidSum";
import { GameIconButton } from "../game-ui";
import { useGame } from "../GameProvider";

const PixiAsteroidSum = dynamic(
  () => import("./PixiAsteroidSum").then((m) => m.PixiAsteroidSum),
  {
    ssr: false,
    loading: () => (
      <div className="flex h-full items-center justify-center bg-slate-950 text-sm text-slate-300">
        Loading asteroids…
      </div>
    ),
  },
);

function AsteroidSumGame({
  challengeKey,
  onPlayAgain,
}: {
  challengeKey: number;
  onPlayAgain: () => void;
}) {
  const { completeActivity, progress } = useGame();
  const sceneRef = useRef<AsteroidSumSceneHandle | null>(null);
  const activityId = "asteroid-sum";
  const difficulty = progress.difficulty;
  const completedRef = useRef(false);

  const challenge = useMemo(
    () => buildAsteroidSumChallenge(difficulty, challengeKey),
    [difficulty, challengeKey],
  );
  const rocketPlacements = useMemo(
    () => rocketPlacementsForDisplay(progress.builtRocket),
    [progress.builtRocket],
  );

  const [currentSum, setCurrentSum] = useState(0);
  const [feedback, setFeedback] = useState<"idle" | "wrong" | "right">("idle");
  const sumRef = useRef(0);

  useEffect(() => {
    sumRef.current = 0;
    setCurrentSum(0);
    setFeedback("idle");
    completedRef.current = false;
    sceneRef.current?.resetRound();
    sceneRef.current?.setPaused(false);
  }, [challenge.target, challenge.maxPart, challenge.seed, difficulty, challengeKey]);

  const handleCatch = useCallback(
    (value: number) => {
      if (feedback === "right") return;

      const next = sumRef.current + value;
      if (next === challenge.target) {
        if (completedRef.current) return;
        completedRef.current = true;
        sumRef.current = next;
        setCurrentSum(next);
        setFeedback("right");
        sceneRef.current?.setPaused(true);
        playSound("correct");
        completeActivity(activityId, difficulty);
        return;
      }

      if (next > challenge.target) {
        setFeedback("wrong");
        playSound("wrong");
        sceneRef.current?.setPaused(true);
        return;
      }

      sumRef.current = next;
      setCurrentSum(next);
      playSound("tap");
    },
    [challenge.target, completeActivity, difficulty, feedback],
  );

  const handleTryAgain = () => {
    sumRef.current = 0;
    setCurrentSum(0);
    setFeedback("idle");
    completedRef.current = false;
    sceneRef.current?.resetRound();
    sceneRef.current?.setPaused(false);
  };

  return (
    <div className="relative flex h-full min-h-0 w-full flex-col overflow-hidden bg-gradient-to-b from-slate-950 to-indigo-950 text-white">
      <div className="shrink-0 px-3 py-2 text-center">
        <div className="flex items-center justify-center gap-3">
          <div className="flex items-baseline gap-1.5">
            <span className="text-3xl font-black text-violet-200">{currentSum}</span>
            <span className="text-lg font-bold text-slate-500">/</span>
            <span className="text-3xl font-black text-amber-300">{challenge.target}</span>
          </div>
        </div>
        {feedback === "wrong" && (
          <p className="mt-1 text-xs font-semibold text-rose-300">Too high!</p>
        )}
      </div>

      <div className="relative min-h-0 flex-1 overflow-hidden">
        <PixiAsteroidSum
          target={challenge.target}
          maxPart={challenge.maxPart}
          seed={challenge.seed}
          fallSpeed={challenge.fallSpeed}
          spawnIntervalMs={challenge.spawnIntervalMs}
          placements={rocketPlacements}
          getCurrentSum={() => sumRef.current}
          onSceneReady={(handle) => {
            sceneRef.current = handle;
          }}
          onCatch={handleCatch}
        />
      </div>

      {feedback === "wrong" && (
        <div className="shrink-0 border-t border-slate-700/50 bg-slate-900/80 py-3">
          <div className="flex justify-center">
            <GameIconButton
              icon="retry"
              tone="sky"
              size="md"
              label="Try again"
              onClick={handleTryAgain}
            />
          </div>
        </div>
      )}

      <ActivitySuccessOverlay
        show={feedback === "right"}
        title="Target reached!"
        subtitle={`${challenge.target} = mission complete`}
        theme="slate"
        rewardLabel={formatStarReward(difficulty)}
        onPlayAgain={onPlayAgain}
      />
    </div>
  );
}

export function AsteroidSumActivity() {
  const { progress, setDifficulty, trackActivitySession } = useGame();
  const [challengeKey, setChallengeKey] = useState(0);

  useEffect(() => {
    trackActivitySession("asteroid-sum");
  }, [trackActivitySession]);

  const handleNewChallenge = () => {
    setChallengeKey((k) => k + 1);
  };

  return (
    <ActivityLayout
      title="Asteroid Sum"
      showRocketBuddy={false}
      headerExtra={
        <div className="flex items-center gap-2">
          <p className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            Difficulty
          </p>
          <DifficultyPicker
            value={progress.difficulty}
            onChange={(level) => {
              setDifficulty(level);
              handleNewChallenge();
            }}
            compact
          />
        </div>
      }
    >
      <AsteroidSumGame
        key={`${progress.difficulty}-${challengeKey}`}
        challengeKey={challengeKey}
        onPlayAgain={handleNewChallenge}
      />
    </ActivityLayout>
  );
}
